// Trang Kho ảnh — STT 18 (URL /kho-anh). Anh Long đặt hàng 01/09/2026.
// Gom TOÀN BỘ ảnh user đã nộp qua các chiến dịch (reels / họp / live / thị trường / spa)
// thành thư viện cá nhân: tab theo loại, xem theo Tháng hoặc Ngày (kèm số lượng),
// bấm ảnh → xem chi tiết + tải xuống bản GỐC.
// Tốc độ: lưới hiện bản /thumb 320px (~20-60KB), chỉ khi mở chi tiết mới tải ảnh gốc.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SystemHeader from '../SystemHeader';
import {
  ReelsAutoCheckAPI, ReelTrendingAPI, LivestreamAPI,
  TeamMeetingAPI, MarketAPI, SpaMarketAPI,
} from '../../services/feature-api';
import { cdnThumb, retryOriginalOnce, downloadImage } from '../../utils/CdnImage';
import { X, Download, Image as ImageIcon } from 'lucide-react';

const ACCENT = '#0EA5E9';

// Tab theo loại chiến dịch (anh Long chốt: Tất cả / Reels / Họp / Live / TT / Spa)
const TABS = [
  { key: 'all', label: 'Tất cả' },
  { key: 'reels', label: 'Reels' },
  { key: 'hop', label: 'Họp' },
  { key: 'live', label: 'Live' },
  { key: 'tt', label: 'TT' },
  { key: 'spa', label: 'Spa' },
];

const TAB_LABEL = { reels: 'Reels', hop: 'Họp team', live: 'Livestream', tt: 'Thị trường', spa: 'Thị trường Spa' };

const toDateStr = (d) => { if (!d) return ''; const dt = typeof d === 'string' ? new Date(d) : d; if (isNaN(dt.getTime())) return ''; return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
const itemDateStr = (item) => { if (!item) return ''; if (item.date) return typeof item.date === 'string' && item.date.length >= 10 ? item.date.slice(0, 10) : toDateStr(item.date); if (item.time) return toDateStr(item.time); if (item.createdAt) return toDateStr(item.createdAt); return ''; };

// Mỗi bản ghi chiến dịch có thể chứa NHIỀU field ảnh — liệt kê đủ để không sót
// (market 5 ảnh, spa 4 ảnh — xem featureDisplayConfig từng form).
const SOURCES = [
  { tab: 'reels', api: ReelsAutoCheckAPI, fields: ['thumbnailUrl'] },
  { tab: 'reels', api: ReelTrendingAPI, fields: ['proofImage'] },
  { tab: 'hop', api: TeamMeetingAPI, fields: ['proofImage'] },
  { tab: 'live', api: LivestreamAPI, fields: ['dateProofImage', 'productPhoto'] },
  { tab: 'tt', api: MarketAPI, fields: ['startPhoto', 'endPhoto', 'customerSupportPhoto', 'productSalesPhoto', 'appOrderPhoto'] },
  { tab: 'spa', api: SpaMarketAPI, fields: ['spaImage', 'spaProductPhoto', 'invoicePhoto', 'wholesaleOrderPhoto'] },
];

const pickRows = (res) => { if (!res?.success) return []; const d = res.data; if (Array.isArray(d)) return d; return d?.rows || d?.items || []; };

// Ô thumb trong lưới — luôn tải bản /thumb; thumb hỏng thử gốc 1 lần rồi placeholder
const Thumb = ({ photo, onClick }) => (
  <button onClick={onClick} className="block w-full overflow-hidden rounded-xl" style={{ aspectRatio: '1', background: '#0d1f35' }}>
    <img src={cdnThumb(photo.url)} alt="" loading="lazy" decoding="async"
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      onError={(e) => {
        if (retryOriginalOnce(e.target)) return;
        const wrap = e.target.parentElement;
        wrap.innerHTML = '';
        const div = document.createElement('div');
        div.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:22px;background:linear-gradient(135deg,#0EA5E933,#9333ea22)';
        div.textContent = '📸';
        wrap.appendChild(div);
      }} />
  </button>
);

// Modal chi tiết: ảnh GỐC full chất lượng + nguồn + ngày + nút tải xuống
const PhotoModal = ({ photo, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  if (!photo) return null;
  const doDownload = async () => {
    setDownloading(true); setError('');
    try {
      const [y, m, d] = (photo.date || '').split('-');
      const ext = (photo.url.split('.').pop() || 'jpg').split('?')[0];
      await downloadImage(photo.url, `${TAB_LABEL[photo.tab] || 'anh'}-${d || ''}${m ? '-' + m : ''}${y ? '-' + y : ''}.${ext}`.replace(/\s+/g, '-'));
    } catch {
      setError('Không tải được ảnh, thử lại sau');
    }
    setDownloading(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="text-sm font-black text-white">{TAB_LABEL[photo.tab] || 'Ảnh'}</div>
          <div className="text-[11px] text-white/50 font-bold">
            {photo.date ? photo.date.split('-').reverse().join('/') : ''}
          </div>
        </div>
        <button onClick={onClose} className="text-white/70 p-1"><X size={24} /></button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-2" onClick={(e) => e.stopPropagation()}>
        {/* Ảnh GỐC — đây là chỗ duy nhất tải bản full (xem chi tiết + làm tư liệu) */}
        <img src={photo.url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 12 }} />
      </div>
      <div className="px-4 py-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {error && <div className="text-center text-xs font-bold text-red-400 mb-2">{error}</div>}
        <button onClick={doDownload} disabled={downloading}
          className="w-full flex items-center justify-center gap-2 text-sm font-black py-3 rounded-xl text-white"
          style={{ background: `linear-gradient(135deg, ${ACCENT}, #9333ea)`, opacity: downloading ? 0.6 : 1 }}>
          <Download size={16} /> {downloading ? 'Đang tải...' : 'Tải xuống ảnh gốc'}
        </button>
      </div>
    </div>
  );
};

const KhoAnhPage = ({ userInfo, accessToken, onLogout, totalPoints, onRefreshPoints }) => {
  const [photos, setPhotos] = useState([]);
  const [loadedCount, setLoadedCount] = useState(0); // số nguồn đã trả lời (render dần)
  const [tab, setTab] = useState('all');
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'day'
  const [selected, setSelected] = useState(null);
  // Anh Long chốt 01/09: KHÔNG bắt bấm từng kỳ — mọi kỳ hiện NỐI TIẾP nhau như gallery
  // điện thoại. groupLimit = số kỳ đã render, tự tăng khi cuộn gần đáy (chống nghẽn DOM
  // khi chế độ Ngày có cả trăm ngày × nghìn ảnh).
  const [groupLimit, setGroupLimit] = useState(15);
  const sentinelRef = useRef(null);

  // Tải TOÀN BỘ lịch sử 6 nguồn 1 lần (không filter tháng) — mỗi nguồn về là gom ảnh ngay
  useEffect(() => {
    if (!accessToken) return;
    let alive = true;
    SOURCES.forEach((src) => {
      src.api.getList(accessToken, {}).then((res) => {
        if (!alive) return;
        const batch = [];
        for (const row of pickRows(res)) {
          const date = itemDateStr(row);
          for (const f of src.fields) {
            const url = row[f];
            if (url && typeof url === 'string' && url.trim()) batch.push({ url, tab: src.tab, date });
          }
        }
        if (batch.length) setPhotos((prev) => [...prev, ...batch]);
        setLoadedCount((n) => n + 1);
      });
    });
    return () => { alive = false; };
  }, [accessToken]);

  const loading = loadedCount < SOURCES.length;

  const tabCounts = useMemo(() => {
    const c = { all: photos.length };
    for (const p of photos) c[p.tab] = (c[p.tab] || 0) + 1;
    return c;
  }, [photos]);

  const filtered = useMemo(() => (tab === 'all' ? photos : photos.filter((p) => p.tab === tab)), [photos, tab]);

  // Gom theo kỳ: 'YYYY-MM' (Tháng) hoặc 'YYYY-MM-DD' (Ngày), mới nhất trước
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      if (!p.date) continue;
      const key = viewMode === 'month' ? p.date.slice(0, 7) : p.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered, viewMode]);

  const groupLabel = (key) => {
    if (viewMode === 'month') { const [y, m] = key.split('-'); return `Tháng ${Number(m)}/${y}`; }
    const [y, m, d] = key.split('-');
    return `${d}/${m}/${y}`;
  };

  // Cuộn gần đáy → render thêm 15 kỳ nữa (ảnh trong kỳ vẫn lazy-load riêng từng tấm)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setGroupLimit((n) => n + 15); },
      { rootMargin: '800px' }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [groups.length, groupLimit]);

  const visibleGroups = groups.slice(0, groupLimit);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#060d1a' }}>
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Kho ảnh" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 w-full max-w-md mx-auto px-3 py-3">
        {/* Tabs loại chiến dịch */}
        <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setGroupLimit(15); }}
                className="flex-shrink-0 text-xs font-black px-3 py-1.5 rounded-full"
                style={active
                  ? { background: ACCENT, color: '#06131f' }
                  : { background: '#ffffff0d', color: '#ffffff99', border: '1px solid #ffffff1a' }}>
                {t.label}{tabCounts[t.key] ? ` · ${tabCounts[t.key]}` : ''}
              </button>
            );
          })}
        </div>

        {/* Chế độ xem: Tháng / Ngày */}
        <div className="flex gap-1.5 mt-2">
          {[['month', 'Tháng'], ['day', 'Ngày']].map(([mode, label]) => {
            const active = viewMode === mode;
            return (
              <button key={mode} onClick={() => { setViewMode(mode); setGroupLimit(15); }}
                className="text-xs font-black px-4 py-1.5 rounded-lg"
                style={active
                  ? { background: `${ACCENT}26`, color: ACCENT, border: `1.5px solid ${ACCENT}` }
                  : { background: '#ffffff0d', color: '#ffffff99', border: '1px solid #ffffff1a' }}>
                {label}
              </button>
            );
          })}
        </div>

        {loading && photos.length === 0 && (
          <div className="rounded-2xl p-8 text-center text-sm text-white/50 mt-3" style={{ background: '#0d1f35' }}>
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3"
              style={{ borderColor: `${ACCENT} ${ACCENT} ${ACCENT} transparent` }} />
            Đang gom ảnh các chiến dịch...
          </div>
        )}

        {!loading && photos.length === 0 && (
          <div className="rounded-2xl p-8 text-center mt-3" style={{ background: '#0d1f35', border: `1px dashed ${ACCENT}44` }}>
            <ImageIcon size={28} className="mx-auto mb-2" style={{ color: `${ACCENT}88` }} />
            <div className="text-sm font-bold text-white/60">Chưa có ảnh nào trong kho</div>
            <div className="text-xs text-white/30 mt-1">Ảnh sẽ tự gom từ Reels, Họp, Live, Thị trường, Spa</div>
          </div>
        )}

        {/* Mọi kỳ nối tiếp nhau: mỗi tháng/ngày = tiêu đề + số ảnh + lưới, cuộn liền một mạch */}
        {visibleGroups.map(([key, list]) => (
          <div key={key} className="mt-4">
            <div className="flex items-baseline justify-between px-0.5 mb-2 sticky top-0 z-10 py-1"
              style={{ background: '#060d1a' }}>
              <span className="text-sm font-black text-white">{groupLabel(key)}</span>
              <span className="text-[11px] font-bold" style={{ color: ACCENT }}>{list.length} ảnh</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {list.map((p, i) => (
                <Thumb key={`${p.url}-${i}`} photo={p} onClick={() => setSelected(p)} />
              ))}
            </div>
          </div>
        ))}

        {/* Mốc vô hình gần đáy — chạm tới là render thêm kỳ cũ hơn */}
        {groupLimit < groups.length && <div ref={sentinelRef} className="h-8" />}
      </div>

      <PhotoModal photo={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default KhoAnhPage;
